<template>
    <div class="btn-toolbar justify-content-between mt-3" role="toolbar">
        <div class="col-sm-12 col-md-6 col-xl-4 mb-2">
            <div v-if="loaded">
                <div
                    class="d-flex flex-row"
                    aria-describedby="eventFilterHelpBlock"
                >
                    <vs-radio v-for="(filter,index) in settings.itemFilters"
                        :key="index"
                        v-model="settings.selectedFilter"
                        vs-name="calendarItemsFilterRadio"
                        :vs-value="filter.id"
                    >
                    <span class="mr-1" v-cloak>{{ isEnVersion ? filter.textEn : filter.text }}</span>
                    </vs-radio>
                </div>
                <small id="eventFilterHelpBlock" class="form-text text-muted">
                    {{ $t('calendar.eventFilter') }}
                </small>

                <div v-if="filterByDirectorate">
                    <nomenclature-dropdown :loadByParentSort="true"
                                           :parentSort="directorateSort"
                                           :fieldName="$t('administrativePosition.directorate')"
                                           v-model="settings.directorateId"
                                           :isCity="false"
                                           aria-describedby="directorateHelpBlock"></nomenclature-dropdown>
                    <span class="text-danger col-12" v-if="!isValidProposalLegalArea">{{ $t('validation.requiredField') }}</span>
                    <small id="directorateHelpBlock" class="form-text text-muted">
                        {{ $t('administrativePosition.directorate') }}
                    </small>
                </div>
            </div>
            <div class="loader" v-else>

            </div>
        </div>

        <div class="col-sm-12 col-md-6 col-xl-4 mb-2">
            <div v-if="loaded">
                <div class="d-flex flex-row"
                     aria-describedby="calendarItemFilterHelpBlock">
                    <vs-checkbox v-for="(type) in settings.itemTypes"
                                 :key="type.id"
                                 :color=" type.color"
                                 v-model="type.checked">
                        <span :style="{borderBottom: '2px solid',  borderColor: type.color }" v-cloak>{{ isEnVersion ? type.textEn : type.text }}</span>
                    </vs-checkbox>
                </div>
                <small id="calendarItemFilterHelpBlock" class="form-text text-muted">
                    {{ $t('calendar.itemFilter') }}
                </small>
            </div>
            <div class="loader" v-else></div>
        </div>

        <div class="col-sm-12 col-md-6 col-xl-4 mb-2" v-show="showTaskStatuses">
            <div v-if="loaded">
                <div class="d-flex flex-row"
                     aria-describedby="taskFilterHelpBlock">
                    <vs-checkbox v-for="(status) in settings.taskStatuses"
                                 :key="status.id"
                                 v-model="status.checked">
                        <span v-cloak>{{ isEnVersion ? status.textEn : status.text }}</span>
                    </vs-checkbox>
                </div>
                <small id="taskFilterHelpBlock" class="form-text text-muted">
                    {{ $t('calendar.taskFilter') }}
                </small>
            </div>
            <div class="loader" v-else></div>
        </div>

        <div class="col-sm-12 col-md-6 col-xl-4 mb-2">
            <div class="btn-group" v-if="loaded">
                <button class="btn btn-secondary"
                        @click.stop="loadLoggedUserSettings">
                    <i class="fas fa-user-cog"></i>
                    {{ $t('buttons.loadUserSettings.btnText') }}
                </button>
                <button class="btn btn-secondary"
                        @click.stop="saveLoggedUserSettings">
                    <i class="fas fa-save"></i>
                    {{ $t('buttons.saveGridOptions.btnText') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { CalendarSettingsModel } from '../../models/models';
    import { Culture, NomenclatureParentBySort } from '../../models/enums';
    import NomenclatureDropdown from '../../components/dropdown/NomenclatureDropdown';

    export default {
        components: {
            NomenclatureDropdown
        },
        props: {

        },
        data() {
            return {
                name: 'CalendarControls',
                settings: new CalendarSettingsModel(),
                loaded: false,
                directorateSort: NomenclatureParentBySort.Directorate
            }
        },
        methods: {
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            loadLoggedUserSettings() {
                this.loaded = false;
                this.$http.get('/api/calendar/calendarSettingsForLoggedUser').then(result => {
                    if (result.data.settings) {
                        this.settings = new CalendarSettingsModel(result.data.settings);
                    } else {
                        this.settings = new CalendarSettingsModel();
                    }

                    this.loaded = true;
                    this.$emit('reloaded');
                }).catch(error => {
                    this.loaded = true;
                    this.settings = new CalendarSettingsModel();
                    this.$emit('reloaded');

                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                });
            },
            saveLoggedUserSettings() {
                this.loaded = false;
                this.$http.post('/api/calendar/saveSettingsForLoggedUser', this.settings).then(response => {
                    this.loaded = true;
                    this.$emit('reloaded');

                    this.$vs.notify({
                        title: this.$t('buttons.saveGridOptions.btnText'),
                        text: this.$helpers.responseMessage(response),
                        color: 'success',
                        time: this.$store.getters.notificationDuration,
                    })
                }).catch(error => {
                    this.loaded = true;
                    this.$emit('reloaded');

                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    });
                });
            },
            calendarUserSettings() {
                return this.settings;
            }
        },
        mounted() {
            this.loadLoggedUserSettings();
        },
        computed: {
            token: function () {
                return this.$store.getters.authToken;
            },
            isEnVersion() {
                return this.$store.getters.currentCulture === Culture.EN;
            },
            isValidProposalLegalArea: function () {
                return this.settings.selectedFilter !== 3 || this.settings.directorateId;
            },
            filterByDirectorate() {
                return this.settings.selectedFilter === 3;
            },
            showTaskStatuses() {
                if (!this.settings || !Array.isArray(this.settings.itemTypes)) {
                    return false;
                }

                let toShow = this.settings.itemTypes.filter(x => x.textEn.includes('Task') >= 0 && x.checked);
                return toShow.length > 0;
            }
        }

    }
</script>

<style scoped>
    .invalid-slot {
        background: red !important;
        cursor: no-drop;
    }

    /* Hide toolbar, navigation and footer during export */
    .k-pdf-export .k-scheduler-toolbar,
    .k-pdf-export .k-scheduler-navigation .k-nav-today,
    .k-pdf-export .k-scheduler-navigation .k-nav-prev,
    .k-pdf-export .k-scheduler-navigation .k-nav-next,
    .k-pdf-export .k-scheduler-footer {
        display: none;
    }
</style>
